
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { TrendingUp, Globe, Shield, Clock, DollarSign, Users, CheckCircle, X } from 'lucide-react';

export default function RWAFinancingPage() {
  const benefits = [
    {
      icon: TrendingUp,
      title: 'Lower Entry Barriers',
      description: 'Fractional ownership enables smaller investors to participate in high-value assets.'
    },
    {
      icon: DollarSign,
      title: 'Improved Liquidity',
      description: '24/7 trading capabilities transform illiquid assets into tradeable instruments.'
    },
    {
      icon: Shield,
      title: 'Enhanced Transparency',
      description: 'Blockchain technology provides immutable records and real-time asset tracking.'
    },
    {
      icon: CheckCircle,
      title: 'Regulatory Compliance',
      description: 'Built-in compliance features ensure adherence to local and international regulations.'
    }
  ];

  const comparison = [
    {
      aspect: 'Processing Speed',
      traditional: 'Weeks to months',
      rwa: 'Days to weeks',
      advantage: 'rwa'
    },
    {
      aspect: 'Transaction Costs',
      traditional: 'High (5-15%)',
      rwa: 'Low (1-3%)',
      advantage: 'rwa'
    },
    {
      aspect: 'Transparency',
      traditional: 'Limited visibility',
      rwa: 'Full transparency',
      advantage: 'rwa'
    },
    {
      aspect: 'Accessibility',
      traditional: 'High minimums',
      rwa: 'Fractional ownership',
      advantage: 'rwa'
    },
    {
      aspect: 'Trading Hours',
      traditional: 'Business hours only',
      rwa: '24/7 availability',
      advantage: 'rwa'
    },
    {
      aspect: 'Geographic Reach',
      traditional: 'Local/regional',
      rwa: 'Global markets',
      advantage: 'rwa'
    }
  ];

  const beneficiaries = [
    {
      title: 'Real Estate Developers',
      description: 'Access global capital for property development projects through tokenized real estate assets.',
      icon: '🏢'
    },
    {
      title: 'Manufacturers',
      description: 'Finance equipment, inventory, and production lines with improved capital efficiency.',
      icon: '🏭'
    },
    {
      title: 'Supply Chain Businesses',
      description: 'Optimize working capital through tokenized inventory and receivables financing.',
      icon: '📦'
    },
    {
      title: 'Asset Managers',
      description: 'Create new investment products and improve portfolio liquidity for clients.',
      icon: '💼'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section 
          className="relative py-20 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(30, 64, 175, 0.9), rgba(30, 64, 175, 0.7)), url('https://public.readdy.ai/ai/img_res/a49e66f377e7a52e0b3e8b46d0898f5d.jpg')`
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <h1 className="text-5xl font-bold mb-6">What is RWA Financing?</h1>
              <p className="text-xl max-w-4xl mx-auto leading-relaxed">
                Understanding Real World Asset financing and how it's revolutionizing 
                access to global capital markets for SMEs worldwide.
              </p>
            </div>
          </div>
        </section>

        {/* Definition Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Defining RWA Financing
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  <strong>Real World Asset (RWA) financing</strong> refers to the process of bringing 
                  traditional, tangible assets onto the blockchain through tokenization. This innovative 
                  approach transforms physical assets like real estate, equipment, invoices, and commodities 
                  into digital tokens that can be traded, fractionalized, and used as collateral.
                </p>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  By bridging the gap between traditional finance and decentralized finance (DeFi), 
                  RWA financing unlocks new liquidity sources and creates more efficient capital markets 
                  for businesses of all sizes.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-700"><strong>Tokenization:</strong> Converting physical assets into digital tokens</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-700"><strong>Fractionalization:</strong> Enabling partial ownership of high-value assets</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-700"><strong>Global Access:</strong> Connecting local assets to international capital</p>
                  </div>
                </div>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Conceptual%20illustration%20showing%20traditional%20physical%20assets%20like%20buildings%2C%20machinery%2C%20and%20documents%20being%20transformed%20into%20digital%20blockchain%20tokens%2C%20modern%20fintech%20visualization%20with%20blue%20and%20gold%20elements%2C%20clean%20professional%20design&width=600&height=500&seq=rwa-definition-001&orientation=portrait"
                  alt="RWA Tokenization Process"
                  className="rounded-xl shadow-lg object-cover w-full h-96"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Global Trend Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Global RWA Adoption Trends
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Leading financial centers worldwide are embracing RWA financing as the future of capital markets.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="bg-white rounded-xl p-8 shadow-lg text-center">
                <div className="text-4xl mb-4">🇺🇸</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">United States</h3>
                <p className="text-gray-600">
                  Leading regulatory frameworks and institutional adoption of tokenized assets, 
                  with major financial institutions launching RWA products.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg text-center">
                <div className="text-4xl mb-4">🇸🇬</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Singapore</h3>
                <p className="text-gray-600">
                  Progressive regulatory sandbox and strong government support for blockchain 
                  innovation in financial services and asset tokenization.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg text-center">
                <div className="text-4xl mb-4">🇭🇰</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Hong Kong</h3>
                <p className="text-gray-600">
                  Strategic positioning as Asia's digital asset hub with clear regulatory 
                  guidelines for tokenized securities and RWA trading.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Market Growth Projections</h3>
                <p className="text-lg text-gray-600">
                  The global RWA tokenization market is experiencing unprecedented growth
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-3xl font-bold text-blue-600 mb-2">$2.3T</div>
                  <p className="text-gray-600">Projected market size by 2030</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-yellow-600 mb-2">45%</div>
                  <p className="text-gray-600">Annual growth rate (CAGR)</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-green-600 mb-2">150+</div>
                  <p className="text-gray-600">Countries with RWA initiatives</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Benefits of RWA Financing
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover how RWA financing transforms traditional asset management and capital access.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                RWA vs Traditional Financing
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See how RWA financing compares to traditional financing methods across key metrics.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Aspect</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Traditional Financing</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">RWA Financing</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {comparison.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.aspect}</td>
                        <td className="px-6 py-4 text-sm text-center text-gray-600">{item.traditional}</td>
                        <td className="px-6 py-4 text-sm text-center">
                          <span className={`font-medium ${item.advantage === 'rwa' ? 'text-green-600' : 'text-gray-600'}`}>
                            {item.rwa}
                          </span>
                          {item.advantage === 'rwa' && (
                            <CheckCircle className="w-4 h-4 text-green-600 inline ml-2" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Who Can Benefit */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Who Can Benefit from RWA Financing?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                RWA financing opens new opportunities across diverse industries and business models.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {beneficiaries.map((beneficiary, index) => (
                <div key={index} className="bg-gradient-to-r from-blue-50 to-yellow-50 rounded-xl p-8">
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl">{beneficiary.icon}</div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{beneficiary.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{beneficiary.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing Statement */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6">
              RWA is the Bridge to Global Capital Markets
            </h2>
            <p className="text-xl mb-8 leading-relaxed">
              Real World Asset financing represents the future of capital access, connecting traditional 
              enterprises to the global digital economy. By tokenizing real-world assets, businesses can 
              unlock new liquidity sources, reduce financing costs, and access international investors 
              like never before.
            </p>
            <button 
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Explore RWA Opportunities
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
